import React, { useMemo, useState } from 'react'
import { AlertTriangle, Plus, Trash2 } from 'lucide-react'
import { authorizedGraphQLRequest } from '@/api/graphqlClient'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import {
  BADGE_POSITIONS,
  BADGE_TONES,
  PlanCard,
  mergeOverrides,
  resolveText,
  type BlockOverride,
  type CardBadge,
  type CardContent,
  type CardLanguage,
  type CardOverrides,
  type CardPlan,
  type FeatureItem,
  type LocalizedText,
} from '@/components/PlanCard'
import LocalizedInput, { ErrorFieldContext } from '@/pages/PlanCards/LocalizedInput'
import { LanguageSwitch } from '@/pages/PlanCards/TemplateEditor'
import { BLOCK_LABELS, SET_PLAN_CARD, blankText, serverError, type PlanCardTemplate } from '@/pages/PlanCards/editing'

/**
 * One plan's card on the Plans tab: which template draws it, and the text this plan
 * changes on that template. A field left empty takes the template's text. Saving
 * changes only how the plan looks on the paywall, never what it charges.
 */

export interface PlanCardFields {
  cardTemplateId: number | null
  cardTemplateName: string | null
  cardOverrides: CardOverrides | null
  card: CardContent
}

interface PlanCardPanelProps {
  planId: number
  plan: CardPlan
  current: PlanCardFields
  /** Every template, archived included: a plan may keep an archived one. */
  templates: PlanCardTemplate[]
  defaultCard: CardContent | null
  onSaved: (fields: PlanCardFields) => void
}

const TONE_LABELS: Record<CardBadge['tone'], string> = { highlight: 'Lime', outline: 'White', subtle: 'Soft' }

/** Blank texts and empty block entries are dropped: an empty field inherits. */
const cleanOverrides = (overrides: CardOverrides): CardOverrides | null => {
  const blocks: Record<string, BlockOverride> = {}
  for (const [id, fields] of Object.entries(overrides.blocks ?? {})) {
    const kept = Object.fromEntries(Object.entries(fields).filter(([field, v]) => (field === 'items' ? Array.isArray(v) : !blankText(v as LocalizedText))))
    if (Object.keys(kept).length > 0) blocks[id] = kept as BlockOverride
  }
  const out: CardOverrides = {}
  if (overrides.badge !== undefined) out.badge = overrides.badge
  if (Object.keys(blocks).length > 0) out.blocks = blocks
  return Object.keys(out).length > 0 ? out : null
}

const PlanCardPanel: React.FC<PlanCardPanelProps> = ({ planId, plan, current, templates, defaultCard, onSaved }) => {
  const [templateId, setTemplateId] = useState<number | null>(current.cardTemplateId)
  const [overrides, setOverrides] = useState<CardOverrides>(current.cardOverrides ?? {})
  const [language, setLanguage] = useState<CardLanguage>('en')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<{ message: string; field: string | null } | null>(null)

  const base: CardContent | null = templateId === null ? defaultCard : (templates.find((t) => t.id === templateId)?.content ?? null)
  const cleaned = useMemo(() => cleanOverrides(overrides), [overrides])
  const preview = base ? mergeOverrides(base, cleaned) : current.card
  const choosable = templates.filter((t) => !t.isArchived || t.id === current.cardTemplateId)
  const unused = Object.keys(overrides.blocks ?? {}).filter((id) => base && !base.blocks.some((b) => b.id === id))
  const dirty = templateId !== current.cardTemplateId || JSON.stringify(cleaned) !== JSON.stringify(current.cardOverrides ?? null)

  const setField = (blockId: string, field: keyof BlockOverride, value: BlockOverride[keyof BlockOverride] | undefined) =>
    setOverrides((o) => {
      const entry = { ...(o.blocks?.[blockId] ?? {}) } as Record<string, unknown>
      if (value === undefined) delete entry[field]
      else entry[field] = value
      return { ...o, blocks: { ...(o.blocks ?? {}), [blockId]: entry as BlockOverride } }
    })
  const dropBlock = (blockId: string) =>
    setOverrides((o) => {
      const { [blockId]: _gone, ...rest } = o.blocks ?? {}
      return { ...o, blocks: rest }
    })

  const save = async () => {
    setError(null)
    setSaving(true)
    try {
      const response = await authorizedGraphQLRequest<{ setSubscriptionPlanCard: PlanCardFields }>(SET_PLAN_CARD, { planId, templateId, overrides: cleaned })
      onSaved(response.setSubscriptionPlanCard)
      setOverrides(response.setSubscriptionPlanCard.cardOverrides ?? {})
    } catch (err) {
      setError(serverError(err, "Failed to save the plan's card"))
    } finally {
      setSaving(false)
    }
  }

  const text = (blockId: string, field: keyof BlockOverride, label: string, inherited: LocalizedText | undefined, options: { multiline?: boolean; fallback?: string } = {}) => (
    <div key={`${blockId}.${field}`}>
      <label className="mb-1 block text-[11px] font-medium text-gray-600">{label}</label>
      <LocalizedInput
        language={language}
        path={`overrides.blocks.${blockId}.${field}`}
        value={overrides.blocks?.[blockId]?.[field] as LocalizedText | undefined}
        inherited={resolveText(inherited, language) || options.fallback || '(empty)'}
        multiline={options.multiline}
        optional
        onChange={(value) => setField(blockId, field, value)}
      />
    </div>
  )

  const badgeMode = overrides.badge === undefined ? 'inherit' : overrides.badge === null ? 'none' : 'custom'
  const badge = overrides.badge

  return (
    <ErrorFieldContext.Provider value={error?.field ?? null}>
      <div className="mt-3 grid gap-4 border-t border-gray-100 pt-3 lg:grid-cols-[minmax(0,1fr)_20rem]">
        <div className="space-y-3">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <label className="mb-1 block text-[11px] font-medium text-gray-600">Card design</label>
              <select value={templateId ?? ''} onChange={(e) => setTemplateId(e.target.value ? Number(e.target.value) : null)} className="rounded border border-gray-300 bg-white px-2 py-1.5 text-sm">
                <option value="">Default card</option>
                {choosable.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                    {t.isArchived ? ' (archived)' : ''}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-[11px] font-medium text-gray-600">Language</label>
              <LanguageSwitch value={language} onChange={setLanguage} />
            </div>
          </div>

          <p className="text-xs text-gray-500">Change text for this plan only. Empty fields use the design's text, shown in grey.</p>

          {base && (
            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-[11px] font-medium text-gray-600">Badge</label>
                <div className="inline-flex gap-1 rounded-lg bg-gray-100 p-0.5">
                  {(['inherit', 'none', 'custom'] as const).map((mode) => (
                    <button
                      key={mode}
                      type="button"
                      onClick={() =>
                        setOverrides((o) => {
                          const { badge: _b, ...rest } = o
                          if (mode === 'inherit') return rest
                          if (mode === 'none') return { ...rest, badge: null }
                          return { ...rest, badge: base.badge ?? { text: { en: 'Most popular', fr: 'Le plus populaire' }, tone: 'highlight', position: 'top' } }
                        })
                      }
                      className={cn('rounded-md px-2.5 py-1 text-xs font-medium', badgeMode === mode ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900')}
                    >
                      {mode === 'inherit' ? `From design${base.badge ? '' : ' (none)'}` : mode === 'none' ? 'None' : 'Custom'}
                    </button>
                  ))}
                </div>
                {badge && (
                  <div className="mt-2 flex flex-wrap items-start gap-2">
                    <LocalizedInput
                      className="min-w-48 flex-1"
                      language={language}
                      path="overrides.badge.text"
                      value={badge.text}
                      tokens={false}
                      onChange={(t) => setOverrides((o) => ({ ...o, badge: { ...badge, text: t } }))}
                    />
                    <select
                      value={badge.tone}
                      onChange={(e) => setOverrides((o) => ({ ...o, badge: { ...badge, tone: e.target.value as CardBadge['tone'] } }))}
                      className="rounded border border-gray-300 bg-white px-2 py-1.5 text-xs"
                    >
                      {BADGE_TONES.map((tone) => (
                        <option key={tone} value={tone}>
                          {TONE_LABELS[tone]}
                        </option>
                      ))}
                    </select>
                    <select
                      value={badge.position}
                      onChange={(e) => setOverrides((o) => ({ ...o, badge: { ...badge, position: e.target.value as CardBadge['position'] } }))}
                      className="rounded border border-gray-300 bg-white px-2 py-1.5 text-xs"
                    >
                      {BADGE_POSITIONS.map((position) => (
                        <option key={position} value={position}>
                          {position === 'top' ? 'Top' : 'Corner'}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              {base.blocks.map((block) => {
                switch (block.type) {
                  case 'title':
                  case 'tagline':
                  case 'featuresLabel':
                    return text(block.id, 'text', BLOCK_LABELS[block.type], block.text, { multiline: block.type === 'tagline' })
                  case 'price':
                    return (
                      <div key={block.id} className="grid gap-2 sm:grid-cols-2">
                        {text(block.id, 'periodLabel', 'Period label', block.periodLabel, { fallback: `${plan.renews ? 'per' : 'for one'} ${plan.interval}` })}
                        {text(block.id, 'note', 'Note under the price', block.note)}
                      </div>
                    )
                  case 'features': {
                    const items = overrides.blocks?.[block.id]?.items
                    return (
                      <div key={block.id}>
                        <label className="mb-1 inline-flex items-center gap-1.5 text-[11px] font-medium text-gray-600">
                          <input type="checkbox" checked={!!items} onChange={(e) => setField(block.id, 'items', e.target.checked ? block.items.map((i) => ({ ...i })) : undefined)} />
                          Different features for this plan
                        </label>
                        {items && (
                          <div className="space-y-1.5">
                            {items.map((item, i) => {
                              const setItems = (next: FeatureItem[]) => setField(block.id, 'items', next)
                              return (
                                <div key={i} className="flex items-start gap-2">
                                  <LocalizedInput
                                    className="flex-1"
                                    language={language}
                                    path={`overrides.blocks.${block.id}.items[${i}].text`}
                                    value={item.text}
                                    onChange={(t) => setItems(items.map((it, j) => (j === i ? { ...it, text: t } : it)))}
                                  />
                                  <button
                                    type="button"
                                    onClick={() => setItems(items.filter((_, j) => j !== i))}
                                    className="mt-1 rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-red-600"
                                    title="Remove feature"
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </button>
                                </div>
                              )
                            })}
                            <button
                              type="button"
                              onClick={() => setField(block.id, 'items', [...items, { text: { en: '' } }])}
                              className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                            >
                              <Plus className="h-3 w-3" /> Add feature
                            </button>
                          </div>
                        )}
                      </div>
                    )
                  }
                  case 'cta':
                    return text(block.id, 'label', 'Button', block.label)
                  default:
                    return null
                }
              })}

              {unused.length > 0 && (
                <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800">
                  <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
                  <span className="flex-1">
                    {unused.length} change(s) refer to blocks this design does not have and are not shown.{' '}
                    <button type="button" className="underline" onClick={() => unused.forEach(dropBlock)}>
                      Remove them
                    </button>
                  </span>
                </div>
              )}
            </div>
          )}

          {error && <div className="rounded-lg border border-red-100 bg-red-50 p-2 text-sm text-red-700">{error.message}</div>}

          <div className="flex items-center gap-2">
            <Button size="sm" onClick={save} disabled={saving || !dirty}>
              {saving ? 'Saving…' : 'Save card'}
            </Button>
            <Button size="sm" variant="outline" disabled={saving || !cleaned} onClick={() => setOverrides({})}>
              Reset text to design
            </Button>
          </div>
        </div>

        <div className="rounded-xl bg-[#F7F9F6] px-4 pb-4 pt-6">
          <PlanCard plan={plan} card={preview} language={language} cta={{}} />
        </div>
      </div>
    </ErrorFieldContext.Provider>
  )
}

export default PlanCardPanel
